import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  RemoveCircleOutline as NoChangesIcon,
} from '@mui/icons-material';
import { CompanyContentHealth } from '../../types/contentCompletion';
import { CompanyUpdate } from '../../types/company';
import { enrichCompany, getCompany, updateCompany } from '../../api/companies';
import DiffReviewer from '../../components/common/DiffReviewer/DiffReviewer';

// Fields accepted by the company update endpoint. Enrichment / the full company
// record may include read-only fields (id, date_updated, technologies) that the
// update payload must not carry.
const COMPANY_UPDATE_FIELDS: (keyof CompanyUpdate)[] = [
  'name',
  'website',
  'product_website',
  'ticker',
  'exchange',
  'corporate_status',
  'summary_description',
  'description',
  'quartr_company_id',
  'company_type',
  'feed_sec',
  'feed_news_rss',
  'feed_events',
  'feed_press_release',
  'email_press_feed',
  'press_release_url',
  'website_content_archive_url',
  'attribution_statement',
  'update_technologies',
  'completion_score',
];

const toCompanyUpdate = (data: Record<string, any>): CompanyUpdate => {
  const payload: Record<string, any> = {};
  COMPANY_UPDATE_FIELDS.forEach((field) => {
    if (data[field] !== undefined) {
      payload[field] = data[field];
    }
  });
  return payload as CompanyUpdate;
};

type ItemStatus =
  | 'queued'
  | 'processing'
  | 'ready' // enrichment done, has changes to review
  | 'no_changes'
  | 'reviewed' // user submitted changes
  | 'error';

interface BatchItem {
  companyId: string;
  companyName: string;
  status: ItemStatus;
  originalData?: Record<string, any>;
  enrichedData?: Record<string, any>;
  error?: string;
}

interface BatchEnhancementPanelProps {
  companies: CompanyContentHealth[];
  onClose: () => void;
  /** Called after a company's changes are persisted, so the overview can refresh. */
  onCompanyUpdated: () => void;
}

const STATUS_META: Record<
  ItemStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  queued: { label: 'Queued', color: 'default' },
  processing: { label: 'Processing…', color: 'info' },
  ready: { label: 'Ready to review', color: 'warning' },
  no_changes: { label: 'No changes', color: 'default' },
  reviewed: { label: 'Submitted', color: 'success' },
  error: { label: 'Error', color: 'error' },
};

/**
 * Runs AI enrichment sequentially over the selected companies. Each company is
 * enriched one at a time (so the user can see which one is being processed);
 * as each finishes it becomes available for review. The user reviews and submits
 * each company's changes individually via the shared DiffReviewer dialog — the
 * same review-before-submit contract used on the Company edit tab.
 */
const BatchEnhancementPanel: React.FC<BatchEnhancementPanelProps> = ({
  companies,
  onClose,
  onCompanyUpdated,
}) => {
  const [items, setItems] = useState<BatchItem[]>(() =>
    companies.map((c) => ({
      companyId: c.company_id,
      companyName: c.company_name,
      status: 'queued' as ItemStatus,
    }))
  );
  const [running, setRunning] = useState(true);
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const cancelledRef = useRef(false);

  const updateItem = useCallback((companyId: string, patch: Partial<BatchItem>) => {
    setItems((prev) =>
      prev.map((it) => (it.companyId === companyId ? { ...it, ...patch } : it))
    );
  }, []);

  // Sequential enrichment loop — runs once on mount.
  useEffect(() => {
    cancelledRef.current = false;

    const run = async () => {
      for (const company of companies) {
        if (cancelledRef.current) break;
        updateItem(company.company_id, { status: 'processing' });

        try {
          const [original, enrichResult] = await Promise.all([
            getCompany(company.company_id),
            enrichCompany(company.company_id),
          ]);

          if (cancelledRef.current) break;

          if (enrichResult.error) {
            updateItem(company.company_id, { status: 'error', error: enrichResult.error });
            continue;
          }

          const proposed = enrichResult.proposed_changes || {};
          const newValues: Record<string, any> = {};
          for (const [key, change] of Object.entries(proposed)) {
            if (change && typeof change === 'object' && 'new' in (change as any)) {
              newValues[key] = (change as any).new;
            } else {
              newValues[key] = change;
            }
          }

          if (Object.keys(newValues).length === 0) {
            updateItem(company.company_id, { status: 'no_changes' });
            continue;
          }

          const originalData = original as unknown as Record<string, any>;
          updateItem(company.company_id, {
            status: 'ready',
            originalData,
            enrichedData: { ...originalData, ...newValues },
          });
        } catch (err: unknown) {
          if (cancelledRef.current) break;
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
            (err as Error)?.message ||
            'Enrichment failed';
          updateItem(company.company_id, { status: 'error', error: msg });
        }
      }

      if (!cancelledRef.current) setRunning(false);
    };

    run();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyReview = async (finalData: Record<string, any>) => {
    if (reviewIndex === null) return;
    const item = items[reviewIndex];
    try {
      await updateCompany(item.companyId, toCompanyUpdate(finalData));
      updateItem(item.companyId, { status: 'reviewed' });
      setReviewIndex(null);
      onCompanyUpdated();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to submit changes';
      updateItem(item.companyId, { status: 'error', error: msg });
      setReviewIndex(null);
    }
  };

  const total = items.length;
  const finishedCount = items.filter(
    (it) => it.status !== 'queued' && it.status !== 'processing'
  ).length;
  const processing = items.find((it) => it.status === 'processing');
  const readyCount = items.filter((it) => it.status === 'ready').length;
  const allReviewed =
    !running &&
    items.every((it) => it.status === 'reviewed' || it.status === 'no_changes' || it.status === 'error');

  const reviewItem = reviewIndex !== null ? items[reviewIndex] : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'primary.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AutoFixHighIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          AI Enhancement — {total} compan{total === 1 ? 'y' : 'ies'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose}>
          {allReviewed ? 'Done' : 'Dismiss'}
        </Button>
      </Box>

      <LinearProgress
        variant="determinate"
        value={total === 0 ? 0 : (finishedCount / total) * 100}
        sx={{ mb: 1, borderRadius: 1 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {running ? (
          processing ? (
            <>
              Enhancing {finishedCount + 1}/{total}: <strong>{processing.companyName}</strong>…
            </>
          ) : (
            'Starting…'
          )
        ) : (
          <>
            Enhancement complete. {readyCount > 0 ? `${readyCount} awaiting review.` : 'Review the results below.'}
          </>
        )}
      </Typography>

      {readyCount > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Review each company's proposed changes and submit them one by one.
        </Alert>
      )}

      <Divider sx={{ my: 1 }} />

      <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
        {items.map((item, idx) => {
          const meta = STATUS_META[item.status];
          return (
            <ListItem
              key={item.companyId}
              secondaryAction={
                item.status === 'ready' ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setReviewIndex(idx)}
                  >
                    Review &amp; Submit
                  </Button>
                ) : undefined
              }
            >
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', width: 24, justifyContent: 'center' }}>
                {item.status === 'processing' && <CircularProgress size={18} />}
                {item.status === 'reviewed' && <CheckCircleIcon color="success" fontSize="small" />}
                {item.status === 'no_changes' && <NoChangesIcon color="disabled" fontSize="small" />}
                {item.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
              </Box>
              <ListItemText
                primary={item.companyName}
                secondary={item.status === 'error' ? item.error : undefined}
                secondaryTypographyProps={{ color: 'error' }}
              />
              <Chip label={meta.label} color={meta.color} size="small" variant="outlined" sx={{ mr: 1 }} />
            </ListItem>
          );
        })}
      </List>

      {reviewItem && reviewItem.originalData && reviewItem.enrichedData && (
        <DiffReviewer
          isOpen={reviewIndex !== null}
          originalData={reviewItem.originalData}
          enrichedData={reviewItem.enrichedData}
          onApply={handleApplyReview}
          onCancel={() => setReviewIndex(null)}
          title={`Review AI Enhancement — ${reviewItem.companyName}`}
        />
      )}
    </Paper>
  );
};

export default BatchEnhancementPanel;
